import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Activity, Delete, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { homeFor } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PinPad = ({ onSubmit, busy }) => {
  const [pin, setPin] = useState("");
  const press = (k) => {
    if (k === "C") return setPin("");
    if (k === "⌫") return setPin((p) => p.slice(0, -1));
    if (pin.length < 8) setPin((p) => p + k);
  };
  useEffect(() => {
    const h = (e) => { if (/^\d$/.test(e.key)) press(e.key); else if (e.key === "Backspace") press("⌫"); else if (e.key === "Enter" && pin.length >= 4) onSubmit(pin); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  });
  return (
    <div className="space-y-4">
      <div data-testid="pin-display" className="h-14 flex items-center justify-center gap-2 border border-border bg-input rounded-sm font-mono text-2xl tracking-[0.5em]">
        {pin ? "•".repeat(pin.length) : <span className="text-muted-foreground text-sm tracking-normal">Saisissez votre code PIN</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, "C", 0, "⌫"].map((k) => (
          <button key={k} type="button" onClick={() => press(String(k))} data-testid={`pin-key-${k === "⌫" ? "back" : k}`}
            className={cn("h-14 rounded-sm border font-mono text-xl font-semibold transition-colors active:scale-[0.97]",
              typeof k === "number" ? "bg-secondary border-border hover:bg-accent" : "bg-muted/40 border-border text-muted-foreground hover:text-foreground")}>
            {k === "⌫" ? <Delete className="h-5 w-5 mx-auto" /> : k}
          </button>
        ))}
      </div>
      <Button className="w-full h-12 text-base font-semibold" disabled={pin.length < 4 || busy} onClick={() => onSubmit(pin)} data-testid="pin-submit-button">Connexion opérateur</Button>
    </div>
  );
};

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const go = async (body) => {
    setBusy(true); setError("");
    try { const u = await login(body); toast.success(`Bienvenue, ${u.nom}`); navigate(homeFor(u.role), { replace: true }); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex relative flex-col justify-between p-10 bg-slate-950 border-r border-border overflow-hidden">
        <img src="https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative flex items-center gap-2">
          <div className="h-9 w-9 bg-primary rounded-sm flex items-center justify-center"><Activity className="h-5 w-5 text-primary-foreground" /></div>
          <div><div className="font-bold text-lg tracking-tight">ProdTrack</div><div className="eyebrow">Manufacturing Execution System</div></div>
        </div>
        <div className="relative max-w-md space-y-4">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Pilotage de production<br /><span className="text-primary">& TRS temps réel</span></h1>
          <p className="text-sm text-muted-foreground">Ordres de fabrication, arrêts, rejets, disponibilité · performance · qualité — une seule source de vérité pour l'atelier et la direction.</p>
          <div className="grid grid-cols-3 gap-2 pt-2">
            {[["D", "Disponibilité"], ["P", "Performance"], ["Q", "Qualité"]].map(([k, l]) => (
              <div key={k} className="panel p-2.5"><div className="font-mono text-xl font-bold text-primary">{k}</div><div className="eyebrow">{l}</div></div>
            ))}
          </div>
        </div>
        <div className="relative eyebrow">TM Global Holdings · ProdTrack V2</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-5">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="h-8 w-8 bg-primary rounded-sm flex items-center justify-center"><Activity className="h-4 w-4 text-primary-foreground" /></div>
            <div className="font-bold tracking-tight">ProdTrack</div>
          </div>
          <div>
            <h2 className="text-base md:text-lg font-bold">Connexion</h2>
            <p className="text-xs text-muted-foreground">Accédez à votre espace de pilotage ou au poste atelier.</p>
          </div>
          <div className="grid grid-cols-2 gap-1 p-1 bg-muted/40 border border-border rounded-sm">
            {[["email", Mail, "Email / mot de passe"], ["pin", KeyRound, "PIN opérateur"]].map(([m, Icon, l]) => (
              <button key={m} type="button" onClick={() => { setMode(m); setError(""); }} data-testid={`login-mode-${m}`}
                className={cn("h-8 flex items-center justify-center gap-1.5 rounded-sm text-xs font-medium transition-colors", mode === m ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground")}>
                <Icon className="h-3.5 w-3.5" />{l}
              </button>
            ))}
          </div>
          {mode === "email" ? (
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); go({ email: email.trim().toLowerCase(), password: password.trim() }); }}>
              <div className="space-y-1"><label className="eyebrow">Email</label><Input data-testid="login-email-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@entreprise.com" className="h-10 rounded-sm" /></div>
              <div className="space-y-1"><label className="eyebrow">Mot de passe</label><Input data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-10 rounded-sm" /></div>
              <Button type="submit" className="w-full h-10 font-semibold" disabled={busy || !email || !password} data-testid="login-submit-button">{busy ? "Connexion…" : "Se connecter"}</Button>
            </form>
          ) : <PinPad onSubmit={(pin) => go({ pin })} busy={busy} />}
          {error && <div data-testid="login-error" className="text-xs text-red-300 border border-red-500/40 bg-red-950/30 rounded-sm px-3 py-2 font-mono">{error}</div>}
        </div>
      </div>
    </div>
  );
}
